import React, { useState, useEffect } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { setActivationVisitFlag } from "../ActivationChecklistWidget";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import AnnouncementBanner from "../AnnouncementBanner";
import PWAInstallPrompt from "../PWAInstallPrompt";
import { cn } from "@/lib/utils";

// Routes that count as "choosing a next workspace tool" for the Explore checklist path.
// Visiting any of these signals that the user picked a module to explore.
const WORKSPACE_TOOL_ROUTES = [
  "/clients",
  "/invoices",
  "/time",
  "/financials",
  "/budget",
  "/savings",
  "/tasks",
  "/leads",
  "/reports",
  "/recurring",
];

export const Layout = () => {
  const { isAuthenticated, loading, isSubscriptionActive, isAdmin, trialExpired, user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const location = useLocation();

  // Activation checklist — Explore path: "Choose next workspace tool"
  // When the user (with activation_path === "explore") visits any qualifying module,
  // persist the flag so the checklist item auto-completes.
  // Guard: skip if flag is already set to avoid redundant localStorage writes.
  useEffect(() => {
    if (!user?.id || user?.activation_path !== "explore") return;
    const isQualifying = WORKSPACE_TOOL_ROUTES.some(
      (route) => location.pathname === route || location.pathname.startsWith(route + "/")
    );
    if (isQualifying && localStorage.getItem(`activation_visit_${user.id}_workspace_tool_chosen`) !== "1") {
      setActivationVisitFlag(user.id, "workspace_tool_chosen");
    }
  }, [location.pathname, user?.id, user?.activation_path]);

  // redirect root with referral code straight to registration so the tracking param is preserved
  if (location.pathname === "/" && new URLSearchParams(location.search).get("ref")) {
    return <Navigate to={`/register${location.search}`} replace />;
  }

  // Prevent Layout from running on public pages
  if (
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/register"
  ) {
    return <Outlet />;
  }


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isSubscriptionActive && !isAdmin && trialExpired && location.pathname !== "/settings") {
    return <Navigate to="/paywall" replace />;
  }

  return (
    <div className="min-h-screen bg-background print:bg-white">
      {/* Mobile overlay backdrop */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      <div className="no-print">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
        />
      </div>

      {/* Main content – pushed right by sidebar on lg+, full-width on mobile */}
      <div
        className={cn(
          "min-h-screen transition-all duration-300 print:ml-0",
          "ml-0 lg:ml-64",
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-64"
        )}
      >
        <div className="no-print">
          <Header onMobileMenuToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)} />
          <AnnouncementBanner />
        </div>
        <main className="p-4 sm:p-6 print:p-0 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      <PWAInstallPrompt />
    </div>
  );
};
